import React, { useCallback, useMemo, useState } from 'react';
import cl from 'classnames';
import { DeviceEvent } from 'src/models';
import { ALL_ITEM_ID, HOTELS, HEADSETS } from 'src/constants';
import {
  useContextData,
  useLanguage,
  usePage,
  useXlsxWriter,
  XlsxTableConfig,
} from 'src/hooks';
import { compareString, formatDate } from 'src/helper';
import { useAppDispatch } from 'src/store';
import { useAccountState } from 'src/services/auth';
import {
  getDeviceEventsAction,
  useDeviceEventsState,
  GetDeviceEventsQueryParams,
} from 'src/services/deviceEvents';
import { PageSwitcher, StickyTable, StickyTableConfig } from 'src/components';
import {
  GroupFilter,
  FilterModalButton,
  SearchInputComponent,
  Button,
  Modal,
  Link,
  DateRange,
  CalendarDate,
  useDateRangePicker,
} from 'src/components/general';
import styles from './serviceDeviceEventsStyle.module.scss';

export const ReportsHeadsets = () => {
  const {
    header: { width: headerWidth },
  } = useContextData();
  const {
    access: { accessMap },
  } = usePage();
  const dispatch = useAppDispatch();
  const { user } = useAccountState();
  const { queryParams, events, isPending } = useDeviceEventsState();
  const { writeToXlsx } = useXlsxWriter<DeviceEvent>();
  const { t } = useLanguage();

  const initDateRange: DateRange = useMemo(
    () => ({
      start: new CalendarDate(queryParams.start),
      end: new CalendarDate(queryParams.end),
    }),
    [queryParams.start, queryParams.end],
  );

  const { range, dateRangePicker, modalDateRangePicker, applyDRPChanges } =
    useDateRangePicker({
      inputClassName: styles.datepickerInput,
      modalInputClassName: styles.datepickerModalInput,
      withoutClear: true,
      initValue: initDateRange,
      disabled: isPending,
    });

  const companies = useMemo(() => user?.companies ?? [], [user?.companies]);
  const initCompanies = useMemo(
    () =>
      queryParams.companies
        ? new Set<string>(queryParams.companies.split(','))
        : new Set<string>(),
    [queryParams.companies],
  );

  const [selectedCompany, setSelectedCompany] =
    useState<Set<string>>(initCompanies);
  const [searchEventType, setSearchEventType] = useState(
    queryParams.eventType ?? '',
  );
  const [searchDeviceId, setSearchDeviceId] = useState(
    queryParams.phyDeviceId ?? '',
  );
  const [searchHumanId, setSearchHumanId] = useState(queryParams.humanId ?? '');

  const [showSearchModal, setShowSearchModal] = useState(false);

  const selectAllCompanies = useMemo(
    () => ({
      id: ALL_ITEM_ID,
      label: t('general.allCompanies'),
    }),
    [t],
  );

  const config: StickyTableConfig<DeviceEvent>[] = useMemo(
    () => [
      {
        title: t('page.reports.deviceEvents.date'),
        key: 'date',
        field: 'date',
        sorter: (a, b, sortOrder) => {
          const dateA = new Date(a.date).getTime();
          const dateB = new Date(b.date).getTime();
          return (dateA - dateB) * (sortOrder === 'descend' ? -1 : 1);
        },
        render: (value) => formatDate(value),
      },
      {
        title: t('page.reports.deviceEvents.companyName'),
        key: 'companyName',
        field: 'companyName',
        sorter: (a, b, sortOrder) =>
          compareString(a.companyName, b.companyName) *
          (sortOrder === 'descend' ? -1 : 1),
        render: (value, headset) => (
          <Link
            className={cl(
              styles.companyName,
              (!accessMap[HOTELS].length ||
                companies.every(({ id }) => id !== headset.companyId)) &&
                styles.inactive,
            )}
            src={
              companies.some(({ id }) => id === headset.companyId) &&
              accessMap[HOTELS].length
                ? `/${HOTELS}/${headset.companyId}/${
                    accessMap[HOTELS].includes(HEADSETS)
                      ? HEADSETS
                      : accessMap[HOTELS][0]
                  }`
                : null
            }
          >
            {value}
          </Link>
        ),
      },
      {
        title: t('page.reports.deviceEvents.count'),
        key: 'count',
        field: 'count',
        sorter: (a, b, sortOrder) =>
          (a.count - b.count) * (sortOrder === 'descend' ? -1 : 1),
      },
    ],
    [accessMap, companies, t],
  );

  const xlsxConfig: XlsxTableConfig<DeviceEvent>[] = useMemo(
    () => [
      {
        title: t('page.reports.deviceEvents.date'),
        field: 'date',
        key: 'date',
        render: (value) => formatDate(value),
      },
      {
        title: t('page.reports.deviceEvents.companyName'),
        field: 'companyName',
        key: 'companyName',
      },
      {
        title: t('page.reports.deviceEvents.count'),
        field: 'count',
        key: 'count',
      },
    ],
    [t],
  );

  const selectCompany = useCallback(
    (item: string, value: boolean, selected: Set<string>) => {
      if (item === ALL_ITEM_ID) {
        return selected.size - (selected.has(ALL_ITEM_ID) ? 1 : 0) <
          companies.length
          ? new Set([ALL_ITEM_ID, ...companies.map((item) => item.id)])
          : new Set<string>();
      }

      const updatedIDs = new Set(selected);
      updatedIDs.delete(ALL_ITEM_ID);

      if (value) {
        updatedIDs.add(item);
      } else {
        updatedIDs.delete(item);
      }

      if (updatedIDs.size === companies.length) {
        updatedIDs.add(ALL_ITEM_ID);
      }

      return updatedIDs;
    },
    [companies],
  );

  const apply = useCallback(
    (newRange?: DateRange) => {
      const updRange = newRange ?? range;

      if (
        isPending ||
        !updRange.start ||
        !updRange.end ||
        !searchEventType ||
        (!searchDeviceId && !searchHumanId)
      ) {
        return;
      }

      const params: GetDeviceEventsQueryParams = {
        companies:
          !selectedCompany.size || selectedCompany.has(ALL_ITEM_ID)
            ? undefined
            : [...selectedCompany].join(','),
        phyDeviceId: searchDeviceId.trim() || undefined,
        humanId: searchHumanId.trim() || undefined,
        start: updRange.start.format,
        end: updRange.end.format,
        eventType: searchEventType.trim(),
      };

      dispatch(getDeviceEventsAction({ params }));
    },
    [
      range,
      isPending,
      searchEventType,
      searchDeviceId,
      searchHumanId,
      selectedCompany,
      dispatch,
    ],
  );

  const emptyCell = useMemo(
    () => <>{t('page.reports.headsets.emptyCell')}</>,
    [t],
  );

  return (
    <div className={styles.container}>
      <Modal
        header={t('general.search')}
        className={styles.modal}
        show={showSearchModal}
        close={() => {
          setShowSearchModal(false);
          setSearchEventType(
            queryParams.eventType ?? 'ev_controller_connected',
          );
          setSearchDeviceId(queryParams.phyDeviceId ?? '');
          setSearchHumanId(queryParams.humanId ?? '');
          applyDRPChanges();
        }}
        footer={
          <Button
            label={t('general.apply')}
            onClick={() => {
              apply();
              setShowSearchModal(false);
            }}
            design="lightBlue"
            size="small"
            className={styles.modalActionButton}
            disabled={
              isPending ||
              !searchEventType ||
              (!searchDeviceId && !searchHumanId)
            }
          />
        }
      >
        <div className={styles.modalContent}>
          <GroupFilter
            label={t('general.selectedCompanies')}
            selectedIDs={selectedCompany}
            items={[
              selectAllCompanies,
              ...companies.map((item) => ({
                id: item.id,
                label: item.name,
              })),
            ]}
            selectItem={(item, value) => {
              setSelectedCompany(selectCompany(item, value, selectedCompany));
            }}
            size="small"
            className={styles.modalSelectCompany}
          />
          {modalDateRangePicker}
          <SearchInputComponent
            value={searchEventType}
            setValue={(value) => {
              setSearchEventType(value);
            }}
            placeholder={t('page.reports.deviceEvents.eventType')}
            className={styles.modalSearchInput}
            size="small"
            enterKeyHint="next"
            tabIndex={1}
          />
          <SearchInputComponent
            value={searchHumanId}
            setValue={(value) => {
              setSearchHumanId(value);
            }}
            placeholder={t('page.reports.deviceEvents.humanId')}
            className={styles.modalSearchInput}
            size="small"
            enterKeyHint="next"
            tabIndex={2}
          />
          <SearchInputComponent
            value={searchDeviceId}
            setValue={(value) => {
              setSearchDeviceId(value);
            }}
            placeholder={t('page.reports.deviceEvents.headsetId')}
            className={styles.modalSearchInput}
            size="small"
            enterKeyHint="done"
            tabIndex={3}
          />
        </div>
      </Modal>
      <div
        className={styles.topSection}
        style={{
          minWidth: headerWidth,
          maxWidth: headerWidth,
        }}
      >
        <div className={styles.pageSwitcherContainer}>
          <PageSwitcher
            className={styles.pageSwitcher}
            selectClassName={styles.mobileSelect}
          />
          <div className={styles.filterButtons}>
            <FilterModalButton
              setShow={setShowSearchModal}
              icon="search"
              disabled={isPending}
            />
          </div>
        </div>
        <div className={styles.filtersWrapper}>
          <div className={styles.filters}>
            <GroupFilter
              label={t('general.selectedCompanies')}
              selectedIDs={selectedCompany}
              items={[
                selectAllCompanies,
                ...companies.map((item) => ({
                  id: item.id,
                  label: item.name,
                })),
              ]}
              selectItem={(item, value) => {
                setSelectedCompany(selectCompany(item, value, selectedCompany));
              }}
              className={styles.selectCompany}
              disabled={isPending}
            />
            {dateRangePicker}
            <SearchInputComponent
              value={searchEventType}
              setValue={(value) => {
                setSearchEventType(value);
              }}
              placeholder={t('page.reports.deviceEvents.eventType')}
              className={styles.searchInputLong}
              disabled={isPending}
              onEnter={() => {
                apply();
              }}
            />
            <SearchInputComponent
              value={searchHumanId}
              setValue={(value) => {
                setSearchHumanId(value);
              }}
              placeholder={t('page.reports.deviceEvents.humanId')}
              className={styles.searchInputShort}
              disabled={isPending || Boolean(searchDeviceId)}
              onEnter={() => {
                apply();
              }}
            />
            <SearchInputComponent
              value={searchDeviceId}
              setValue={(value) => {
                setSearchDeviceId(value);
              }}
              placeholder={t('page.reports.deviceEvents.headsetId')}
              className={styles.searchInputLong}
              disabled={isPending || Boolean(searchHumanId)}
              onEnter={() => {
                apply();
              }}
            />
            <Button
              onClick={() => {
                apply();
              }}
              label={t('general.apply')}
              design="white"
              className={styles.actionButton}
              disabled={
                isPending ||
                !searchEventType ||
                (!searchDeviceId && !searchHumanId)
              }
            />
            <Button
              label={t('component.exportToXlsx.button')}
              design="lightBlue"
              className={styles.actionButton}
              icon="exportToXlsx"
              onClick={() => {
                writeToXlsx(
                  {
                    config: xlsxConfig,
                    data: events,
                  },
                  `event_${searchEventType}_device_${searchDeviceId || searchHumanId}.xlsx`,
                  true,
                );
              }}
              disabled={isPending}
            />
          </div>
        </div>
      </div>
      <StickyTable
        config={config}
        data={events}
        withPaginator
        isLoading={isPending}
        className={styles.table}
        isFiltered={false}
        emptyCell={emptyCell}
        autoWidth
      />
    </div>
  );
};

export default ReportsHeadsets;
